
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Trash2, 
  Loader2, 
  AlertTriangle, 
  CheckCircle, 
  Info,
  RefreshCw 
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { 
  getSubscriptionsCleanupSummary, 
  cleanupSubscriptionsWithoutPaymentMethods 
} from '../api/subscriptionCleanupApi';

interface CleanupSummary {
  total_subscriptions_to_cleanup: number;
  total_related_payments: number;
  cancelled_subscriptions: number;
  payment_update_needed_subscriptions: number;
}

interface SubscriptionCleanupPanelProps {
  onCleanupComplete?: () => void;
}

export function SubscriptionCleanupPanel({ onCleanupComplete }: SubscriptionCleanupPanelProps) {
  const [summary, setSummary] = useState<CleanupSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загрузка статистики
  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await getSubscriptionsCleanupSummary();
      if (error) {
        setError(`Ошибка загрузки статистики: ${error}`);
      } else {
        setSummary(data);
      }
    } catch (err: any) {
      setError(`Непредвиденная ошибка: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Выполнение пакетного удаления
  const handleCleanup = async () => {
    setCleanupLoading(true);
    
    try {
      const { success, data, error } = await cleanupSubscriptionsWithoutPaymentMethods();
      
      if (success && data) {
        toast.success(
          `Удаление завершено: ${data.deleted_subscriptions_count} подписок, ${data.deleted_payments_count} платежей`
        );
        
        // Обновляем статистику
        await loadSummary();
        
        // Уведомляем родительский компонент
        if (onCleanupComplete) {
          onCleanupComplete();
        }
      } else {
        toast.error(`Ошибка удаления: ${error}`);
      }
    } catch (err: any) {
      toast.error(`Непредвиденная ошибка: ${err.message}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const hasDataToCleanup = summary && summary.total_subscriptions_to_cleanup > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Очистка подписок
            </CardTitle>
            <CardDescription>
              Удаление подписок без реквизитов карты и связанных отмененных платежей
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadSummary}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : summary ? (
          <div className="space-y-4">
            {/* Статистика */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-destructive">
                  {summary.total_subscriptions_to_cleanup}
                </div>
                <div className="text-sm text-muted-foreground">
                  Подписок к удалению
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {summary.total_related_payments}
                </div>
                <div className="text-sm text-muted-foreground">
                  Связанных платежей
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-medium">
                  {summary.cancelled_subscriptions}
                </div>
                <div className="text-sm text-muted-foreground">
                  <Badge variant="destructive" className="text-xs">Отменено</Badge>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-medium">
                  {summary.payment_update_needed_subscriptions}
                </div>
                <div className="text-sm text-muted-foreground">
                  <Badge variant="secondary" className="text-xs">Нужно обновить</Badge>
                </div>
              </div>
            </div>

            {/* Информационное сообщение */}
            {hasDataToCleanup ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Найдены подписки без привязанных карт, которые можно безопасно удалить. 
                  Это поможет очистить базу данных от неиспользуемых записей.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  База данных чистая! Подписки без реквизитов карты не найдены.
                </AlertDescription>
              </Alert>
            )}

            {/* Кнопка удаления */}
            {hasDataToCleanup && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    disabled={cleanupLoading}
                    className="w-full"
                  >
                    {cleanupLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Удалить все подписки без карт
                  </Button>
                </AlertDialogTrigger>
                
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Подтверждение удаления</AlertDialogTitle>
                    <AlertDialogDescription>
                      Вы собираетесь удалить <strong>{summary.total_subscriptions_to_cleanup}</strong> подписок 
                      и <strong>{summary.total_related_payments}</strong> связанных платежей.
                      <br /><br />
                      Это действие <strong>необратимо</strong>. Продолжить?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Отмена</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleCleanup}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Да, удалить все
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
